import { useEffect, useRef, useState } from "react";

const FINIX_JS_URL = "https://js.finix.com/v/1/finix.js";

let finixPromise: Promise<any> | null = null;

function loadFinixJs(): Promise<any> {
  if (finixPromise) return finixPromise;

  finixPromise = new Promise((resolve, reject) => {
    if ((window as any).Finix) {
      resolve((window as any).Finix);
      return;
    }

    const script = document.createElement("script");
    script.src = FINIX_JS_URL;
    script.async = true;
    script.onload = () => {
      if ((window as any).Finix) {
        resolve((window as any).Finix);
      } else {
        reject(new Error("Finix SDK failed to initialize"));
      }
    };
    script.onerror = () => {
      finixPromise = null;
      reject(new Error("Failed to load Finix SDK script"));
    };
    document.head.appendChild(script);
  });

  return finixPromise;
}

export function useFinixJs() {
  const [finix, setFinix] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadFinixJs()
      .then((sdk) => {
        setFinix(sdk);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setIsLoading(false);
      });
  }, []);

  return { finix, isLoading, error };
}

// Initializes Finix.Auth for on-session fraud detection and returns a getter for the
// current session key. The key must be sent as `fraud_session_id` on Authorization
// and Transfer API requests per Finix's fraud detection guide.
//
// Finix.Auth(env, merchantId) fires an async GET /fraud/sessions under the hood; the
// session key is only available after that resolves. Using the third-arg callback
// captures the key into a ref so getFraudSessionId() returns it reliably even if the
// patient submits the form fast. We also fall back to authRef.getSessionKey() in case
// the callback form isn't supported in some finix.js build.
export function useFinixFraudSession(
  env: "sandbox" | "live" | undefined,
  merchantId: string | undefined
) {
  const { finix } = useFinixJs();
  const authRef = useRef<any>(null);
  const sessionKeyRef = useRef<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    sessionKeyRef.current = null;
    if (!finix || !env || !merchantId) {
      authRef.current = null;
      setReady(false);
      return;
    }
    try {
      authRef.current = finix.Auth(env, merchantId, (sessionKey: string) => {
        if (typeof sessionKey === "string" && sessionKey.length > 0) {
          sessionKeyRef.current = sessionKey;
          setReady(true);
        }
      });
    } catch {
      authRef.current = null;
      setReady(false);
    }
  }, [finix, env, merchantId]);

  const getFraudSessionId = (): string | undefined => {
    if (sessionKeyRef.current) return sessionKeyRef.current;
    if (!authRef.current) return undefined;
    try {
      const key = authRef.current.getSessionKey?.();
      return typeof key === "string" && key.length > 0 ? key : undefined;
    } catch {
      return undefined;
    }
  };

  return { ready, getFraudSessionId };
}
